import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Building2,
  Search,
  RefreshCcw,
  Printer,
  Download,
  Building,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com"; // change if needed

function s(v) {
  return (v ?? "").toString().trim();
}

const MAX_PAGE_BUTTONS = 5;
const getPageWindow = (current, totalPages) => {
  if (totalPages <= MAX_PAGE_BUTTONS) return { start: 1, end: totalPages };

  const half = Math.floor(MAX_PAGE_BUTTONS / 2); // 2
  let start = current - half;
  let end = current + half;

  if (start < 1) {
    start = 1;
    end = MAX_PAGE_BUTTONS;
  }
  if (end > totalPages) {
    end = totalPages;
    start = totalPages - MAX_PAGE_BUTTONS + 1;
  }
  return { start, end };
};

const EmployeeOverallView = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("BranchId") || "";
  const branchType = localStorage.getItem("branchType") || localStorage.getItem("BranchType") || "HO";

  const [branches, setBranches] = useState([]);
  const [defaultBranchId, setDefaultBranchId] = useState("Select");

  // Filter: Branch only
  const [site, setSite] = useState("Select");

  // list + totals
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({
    totalEmployees: 0,
    totalResignation: 0,
    totalCurrent: 0,
  });

  const [loading, setLoading] = useState(false);

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const screenBadge = useMemo(() => "INFRA SCREEN : C-45", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  const loadMeta = async () => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(branchId) || !s(branchType)) {
      toast.warn("Missing login Branch / Branch Type in localStorage.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/employee-overall-view/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType,
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      const list = res.data.branches || [];
      setBranches(list);

      const def = String(res.data.defaultBranchId || "Select");
      setDefaultBranchId(def);

      // ASP default selection = login branch
      setSite(def);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Meta error");
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (p = page) => {
    if (!s(finYearId)) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!s(branchId) || !s(branchType)) {
      toast.warn("Missing login Branch / Branch Type in localStorage.");
      return;
    }

    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/employee-overall-view/data`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType,
          site: site === "Select" ? "" : site,
          page: p,
          pageSize,
        },
      });

      if (res?.data?.success) {
        setRows(res.data.rows || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
        setPage(res.data.page || p);

        setTotals({
          totalEmployees: res.data?.totals?.totalEmployees || 0,
          totalResignation: res.data?.totals?.totalResignation || 0,
          totalCurrent: res.data?.totals?.totalCurrent || 0,
        });
      } else {
        toast.error(res?.data?.message || "Failed to load overall report.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Data load error");
    } finally {
      setLoading(false);
    }
  };

  // init
  useEffect(() => {
    (async () => {
      await loadMeta();
      await fetchList(1);
      setPage(1);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // on page change
  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    setPage(1);
    await fetchList(1);
  };

  const handleViewAll = async () => {
    // ASP sets ddl index = -1 and passes "" => all
    setSite("Select");
    setPage(1);
    await fetchList(1);
  };

  const handlePrint = () => {
    if (!s(finYearId) || !s(branchId) || !s(branchType)) {
      toast.warn("Missing Financial Year / Branch / BranchType");
      return;
    }

    const params = new URLSearchParams({
      finYearId: String(finYearId),
      branchId: String(branchId),
      branchType: String(branchType),
      site: site === "Select" ? "" : site,
    });

    window.open(`/PayRoll/EmployeeOverAllReportPrint?${params.toString()}`, "_blank");
  };

  const handleExcel = () => {
    if (!s(finYearId) || !s(branchId) || !s(branchType)) {
      toast.warn("Missing Financial Year / Branch / BranchType");
      return;
    }

    const params = new URLSearchParams({
      finYearId: String(finYearId),
      branchId: String(branchId),
      branchType: String(branchType),
      site: site === "Select" ? "" : site,
    });

    window.location.href = `${BASE_URL}/api/employee-overall-view/export/excel?${params.toString()}`;
  };

  const showingFrom = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const showingTo = Math.min(page * pageSize, total);

  const { start: pageStart, end: pageEnd } = getPageWindow(page, totalPages);
  const pageNumbers = [];
  for (let p = pageStart; p <= pageEnd; p++) pageNumbers.push(p);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE OVERALL REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${
                  isDark ? "bg-white/10" : "bg-gray-100"
                }`}
              >
                <Building2 className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Employee Overall View
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Branch Filter / Totals / Print / Excel / Pagination
                </div>
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {screenBadge}
              </div>

              <div
                className={`px-4 py-2 rounded-2xl text-sm font-bold ${
                  isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"
                }`}
              >
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(page)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleViewAll} className={ghostBtn} disabled={loading}>
                <RefreshCcw className="w-4 h-4" />
                View All
              </button>

              <button onClick={handlePrint} className={ghostBtn}>
                <Printer className="w-4 h-4" />
                Print
              </button>

              <button onClick={handleExcel} className={primaryBtn}>
                <Download className="w-4 h-4" />
                Excel
              </button>
            </div>
          </div>

          {/* Filter card */}
          <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-end">
              <div>
                <label className={labelBase}>Branch</label>
                <div className="mt-2 flex items-center gap-2">
                  <Building className={`w-5 h-5 ${isDark ? "text-gray-300" : "text-gray-500"}`} />
                  <select
                    value={site}
                    onChange={(e) => setSite(e.target.value)}
                    className={inputBase}
                    disabled={loading}
                  >
                    <option value="Select">Select</option>
                    {branches.map((b) => (
                      <option key={b.fld_branchid} value={String(b.fld_branchid)}>
                        {b.fld_branchname}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex md:justify-end">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </div>

            {/* Totals (like footer in ASP) */}
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className={cardBase + " p-4"}>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>Total Employees</div>
                <div className="text-2xl font-extrabold">{Number(totals.totalEmployees || 0).toFixed(0)}</div>
              </div>
              <div className={cardBase + " p-4"}>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>Total Resignation</div>
                <div className="text-2xl font-extrabold">{Number(totals.totalResignation || 0).toFixed(0)}</div>
              </div>
              <div className={cardBase + " p-4"}>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>C.Total Emp</div>
                <div className="text-2xl font-extrabold">{Number(totals.totalCurrent || 0).toFixed(0)}</div>
              </div>
            </div>
          </form>

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Department</th>
                    <th className="px-4 py-4 text-center font-bold">Total Employees</th>
                    <th className="px-4 py-4 text-center font-bold">Total Resignation</th>
                    <th className="px-4 py-4 text-center font-bold">C.Total Emp</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 5 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={5} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, idx) => (
                      <tr
                        key={`${r.fld_departmentname}-${idx}`}
                        className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}
                      >
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {(page - 1) * pageSize + idx + 1}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.fld_departmentname}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.Noofemployees || 0).toFixed(0)}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.inactiveuser || 0).toFixed(0)}
                        </td>
                        <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {Number(r.activeusers || 0).toFixed(0)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>

                {/* Footer totals row (like ASP GridView footer) */}
                <tfoot>
                  <tr className={isDark ? "bg-gray-900" : "bg-white"}>
                    <td className={`px-4 py-4 text-sm font-extrabold text-center ${isDark ? "text-white" : "text-gray-900"}`} colSpan={2}>
                      Total Employees
                    </td>
                    <td className={`px-4 py-4 text-sm font-extrabold text-center ${isDark ? "text-white" : "text-gray-900"}`}>
                      {Number(totals.totalEmployees || 0).toFixed(0)}
                    </td>
                    <td className={`px-4 py-4 text-sm font-extrabold text-center ${isDark ? "text-white" : "text-gray-900"}`}>
                      {Number(totals.totalResignation || 0).toFixed(0)}
                    </td>
                    <td className={`px-4 py-4 text-sm font-extrabold text-center ${isDark ? "text-white" : "text-gray-900"}`}>
                      {Number(totals.totalCurrent || 0).toFixed(0)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Bottom row: pagination (5 numbers window) */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {pageNumbers.map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      page === p
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    page === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeOverallView;
